// 📂 src/backend/firebase/services/client-service.ts
import { clientDoc, clientsCol } from '@/backend/firebase/services';
import type { Client, ClientInput, ClientWithId } from '@/shared/schema';
import { formatPhoneNumber } from '@/shared/lib/client-utils';
import {
  addDoc,
  getDoc,
  getDocs,
  increment,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';

export const ClientService = {
  /**
   * Busca únicamente en la colección canónica de clientes.
   */
  getById: async (
    ownerId: string,
    clientId: string,
  ): Promise<ClientWithId | null> => {
    const snap = await getDoc(clientDoc(ownerId, clientId));
    if (snap.exists()) {
        const data = snap.data();
        if (!data.audit?.deletedAt) {
            return { ...data, id: snap.id } as ClientWithId;
        }
    }

    return null;
  },

  getByPhoneNumber: async (
    ownerId: string,
    phoneNumber: string,
  ): Promise<ClientWithId[]> => {
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      const q = query(clientsCol(ownerId), where('phone', '==', formattedPhone));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }) as ClientWithId);
    } catch (e) {
      console.error('[ClientService] Error buscando cliente por teléfono:', e);
      throw new Error('Error al buscar por teléfono.');
    }
  },

  getAll: async (ownerId: string): Promise<ClientWithId[]> => {
    // Note: This only gets from 'clients'. Ideally, we should merge or migrate.
    const q = query(
      clientsCol(ownerId),
      where('audit.deletedAt', '==', null),
      orderBy('name', 'asc'),
    );
    const snap = await getDocs(q);
    return snap.docs.map(
      (doc) => ({ ...doc.data(), id: doc.id }) as ClientWithId,
    );
  },

  create: async (ownerId: string, data: ClientInput): Promise<ClientWithId> => {
    const colRef = clientsCol(ownerId);
    const finalData = {
      ...data,
      audit: {
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deletedAt: null,
      },
    };
    const docRef = await addDoc(colRef, finalData as any);
    return { ...finalData, id: docRef.id } as ClientWithId;
  },

  update: async (
    ownerId: string,
    clientId: string,
    data: Partial<Client>,
  ): Promise<void> => {
    const cRef = clientDoc(ownerId, clientId);
    const cSnap = await getDoc(cRef);
    if (cSnap.exists()) {
        await updateDoc(cRef, { ...data, 'audit.updatedAt': serverTimestamp() });
    }
  },

  delete: async (owner_id: string, clientId: string): Promise<void> => {
    const cRef = clientDoc(owner_id, clientId);
    const cSnap = await getDoc(cRef);
    if (cSnap.exists()) {
      await updateDoc(cRef, {
        'audit.deletedAt': serverTimestamp(),
        'audit.updatedAt': serverTimestamp(),
        studentUid: '',
        'academy.isStudent': false,
        'academy.authorizedCourses': [],
      });
    }
  },

  getDeleted: async (ownerId: string): Promise<ClientWithId[]> => {
    const q = query(clientsCol(ownerId), where('audit.deletedAt', '!=', null), orderBy('audit.deletedAt', 'desc'));
    const snap = await getDocs(q);
    return snap.docs.map((doc) => ({ ...doc.data(), id: doc.id }) as ClientWithId);
  },

  restore: async (ownerId: string, clientId: string): Promise<void> => {
    const cRef = clientDoc(ownerId, clientId);
    await updateDoc(cRef, { 'audit.deletedAt': null, 'audit.updatedAt': serverTimestamp() });
  },
};
